/**
 * ReportThreatIntel.js — IOC summary, kill chain positioning, MITRE ATT&CK
 */
import React, { useMemo } from "react";
import { getMitre, getPortInfo, KILL_CHAIN_STAGES, getKillChainStage, SEVERITY_COLORS, severityFromLabel } from "../reportUtils";

export default function ReportThreatIntel({ stats, theme, SEVERITY_COLORS: SC }) {
  if (!stats) return null;

  // Build IOC list from top IPs and attack types
  const iocs = useMemo(() => {
    const list = [];
    stats.srcIPDist.slice(0, 10).forEach(ip => {
      list.push({ type: "IP", value: ip.name, events: ip.value, severity: ip.value > 1000 ? "critical" : ip.value > 100 ? "high" : "medium", description: "Frequent source of attack traffic" });
    });
    stats.portDist.slice(0, 5).forEach(p => {
      const info = getPortInfo(p.name);
      if (info.risk === "critical" || info.risk === "high") {
        list.push({ type: "PORT", value: `${p.name}/${info.service}`, events: p.value, severity: info.risk, description: info.app });
      }
    });
    stats.attackDist.slice(0, 5).filter(a => !["normal","benign","legitimate"].includes(a.name.toLowerCase())).forEach(a => {
      list.push({ type: "SIGNATURE", value: a.name, events: a.value, severity: severityFromLabel(a.name), description: getMitre(a.name).name });
    });
    return list;
  }, [stats]);

  // Kill chain coverage
  const killChainCoverage = useMemo(() => {
    const coverage = new Set();
    stats.attackDist.forEach(a => {
      const mitre = getMitre(a.name);
      const stage = getKillChainStage(mitre.tactic);
      coverage.add(stage);
    });
    return coverage;
  }, [stats]);

  // MITRE technique breakdown
  const mitreBreakdown = useMemo(() => {
    const tactics = {};
    stats.attackDist.forEach(a => {
      const m = getMitre(a.name);
      if (!tactics[m.tactic]) tactics[m.tactic] = { tactic: m.tactic, id: m.id, techniques: [], count: 0 };
      tactics[m.tactic].techniques.push({ name: a.name, technique: m.technique, mitreName: m.name, count: a.value });
      tactics[m.tactic].count += a.value;
    });
    return Object.values(tactics).sort((a, b) => b.count - a.count);
  }, [stats]);

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#C62828">Threat Intelligence</SectionTitle>

      {/* IOC Summary */}
      <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <SubTitle theme={theme}>Indicators of Compromise (IOC)</SubTitle>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
          <thead>
            <tr style={{ background: theme.colors.tableHeader }}>
              {["#","Type","Indicator","Events","Severity","Description"].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 10, letterSpacing: 1 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {iocs.map((ioc, i) => {
              const color = SC[ioc.severity] || SC.info;
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? theme.colors.rowAlt : theme.colors.card, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                  <td style={{ padding: "7px 10px", color: theme.colors.textMuted }}>{i+1}</td>
                  <td style={{ padding: "7px 10px" }}>
                    <span style={{ padding: "2px 6px", background: `${theme.colors.accent}18`, border: "1px solid #1565C020", borderRadius: 4, color: theme.colors.accent, fontSize: 9, fontWeight: 700 }}>{ioc.type}</span>
                  </td>
                  <td style={{ padding: "7px 10px", color: theme.colors.textPrimary, fontWeight: 700, fontFamily: "monospace" }}>{ioc.value}</td>
                  <td style={{ padding: "7px 10px", color: theme.colors.textPrimary }}>{ioc.events.toLocaleString()}</td>
                  <td style={{ padding: "7px 10px" }}>
                    <span style={{ padding: "2px 6px", background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 4, color, fontSize: 9, fontWeight: 700 }}>{ioc.severity.toUpperCase()}</span>
                  </td>
                  <td style={{ padding: "7px 10px", color: theme.colors.textMuted, fontSize: 10 }}>{ioc.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Kill Chain */}
      <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <SubTitle theme={theme}>Kill Chain Positioning (Lockheed Martin)</SubTitle>
        <div style={{ display: "flex", gap: 0, overflowX: "auto", padding: "8px 0" }}>
          {KILL_CHAIN_STAGES.map((stage, i) => {
            const active = killChainCoverage.has(i);
            return (
              <div key={i} style={{ flex: 1, minWidth: 80, textAlign: "center", position: "relative" }}>
                <div style={{
                  background: active ? SC.critical : theme.colors.tableHeader,
                  border: `2px solid ${active ? SC.critical : theme.colors.cardBorder}`,
                  borderRadius: i === 0 ? "8px 0 0 8px" : i === KILL_CHAIN_STAGES.length - 1 ? "0 8px 8px 0" : 0,
                  padding: "14px 8px", marginRight: i < KILL_CHAIN_STAGES.length - 1 ? -1 : 0,
                  position: "relative", zIndex: active ? 1 : 0
                }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>
                    {["🔍","⚙️","📦","💥","🔧","📡","🎯"][i]}
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: 8, fontWeight: 700, color: active ? theme.colors.brandText : theme.colors.textMuted, letterSpacing: 1, textTransform: "uppercase" }}>
                    {stage}
                  </div>
                  {active && (
                    <div style={{ marginTop: 4, fontSize: 9, fontFamily: "monospace", color: "#FFCDD2", fontWeight: 700 }}>DETECTED</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 10, fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>
          {killChainCoverage.size} of {KILL_CHAIN_STAGES.length} kill chain stages detected in this dataset
        </div>
      </div>

      {/* MITRE ATT&CK */}
      <div className="report-card" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20 }}>
        <SubTitle theme={theme}>MITRE ATT&CK® Framework Mapping</SubTitle>
        {mitreBreakdown.map((tactic, ti) => (
          <div key={ti} style={{ marginBottom: 16, padding: 14, background: theme.colors.inputBg, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div>
                <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 900, color: "#C62828" }}>{tactic.tactic}</span>
                <span style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted, marginLeft: 8 }}>{tactic.id}</span>
              </div>
              <span style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textPrimary, fontWeight: 700 }}>{tactic.count.toLocaleString()} events</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {tactic.techniques.map((t, i) => (
                <div key={i} style={{ padding: "4px 10px", background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 6 }}>
                  <span style={{ fontFamily: "monospace", fontSize: 9, color: theme.colors.accent, fontWeight: 700 }}>{t.technique}</span>
                  <span style={{ fontFamily: "monospace", fontSize: 9, color: theme.colors.textMuted, marginLeft: 6 }}>{t.mitreName}</span>
                  <span style={{ fontFamily: "monospace", fontSize: 9, color: "#C62828", marginLeft: 6 }}>({t.count.toLocaleString()})</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", borderLeft: `4px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>{children}</div>
);
const SubTitle = ({ children, theme }) => (
  <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 12, fontWeight: 700 }}>{children}</div>
);
